import { Link } from 'react-router-dom';
import innateMark from '@/assets/innate-mark.png';

const EFFECTIVE_DATE = 'July 21, 2026';
const CONTACT_EMAIL = 'privacy@innatefit.app';

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="text-xl font-bold text-[#5C4A42] mb-3">{title}</h2>
      <div className="text-[#6B5A50] space-y-3 leading-relaxed">{children}</div>
    </section>
  );
}

export default function Privacy() {
  return (
    <div className="min-h-screen bg-[#F5F3EF]">
      <div className="max-w-3xl mx-auto px-4 py-16">
        <Link to="/" className="inline-flex items-center gap-2 mb-8">
          <img src={innateMark} alt="Innate Fitness" className="h-10" />
          <span className="font-black uppercase tracking-tight text-[#5C4A42]">Innate Fitness</span>
        </Link>

        <h1 className="text-3xl md:text-4xl font-black text-[#5C4A42] mb-2">Privacy Policy</h1>
        <p className="text-[#A89284] mb-10">Effective {EFFECTIVE_DATE}</p>

        <Section title="Overview">
          <p>
            This policy describes what information Innate Fitness ("we", "us") collects when you use
            our app or website, why we collect it, who we share it with, and the choices you have. We
            wrote this in plain language on purpose — if anything is unclear, email us at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a>.
          </p>
        </Section>

        <Section title="Information you provide directly">
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Account information:</strong> name, email address, and password (we store only a salted bcrypt hash of your password, never the password itself).</li>
            <li><strong>Health and fitness data you log:</strong> workouts (type, duration, calories), body weight entries, personal and group goals, and progress toward them.</li>
            <li><strong>Meal and grocery data:</strong> grocery items you enter or photograph, and the AI-generated meal plans produced from them.</li>
            <li><strong>Group and social content:</strong> messages you post in group goals, and content you choose to share (e.g. achievement shares, referral codes).</li>
          </ul>
        </Section>

        <Section title="Grocery photo scanning and AI meal generation">
          <p>
            If you use the grocery photo-scan or AI meal-planning features, the photo or grocery list
            you submit is sent to <strong>Google's Gemini API</strong> to identify items and generate a
            meal plan. Google processes this data under its own API terms; we do not control how Google's
            infrastructure handles that request beyond the API call itself. We store the resulting meal
            plan (ingredients, instructions, nutrition estimates) in your account, but we do not store the
            submitted photo itself after the scan completes.
          </p>
        </Section>

        <Section title="Analytics and session recording">
          <p>
            We use <strong>PostHog</strong>, a third-party analytics service, to understand how the app is
            used and to fix bugs. PostHog collects standard usage analytics (page views, feature usage,
            device/browser type) and, on the web version of the app, <strong>may record your on-screen
            session</strong> (clicks, scrolling, and page content) to help us diagnose problems and improve
            the product. Session recordings are configured to capture visible page content and can include
            content rendered in embedded frames.
          </p>
          <p>
            We do not deliberately direct PostHog to capture password fields, and standard input masking
            applies to password inputs by default. However, because session recording captures visible page
            content broadly, avoid entering sensitive information you would not want included in a usage
            recording. PostHog data is used solely to improve Innate Fitness and is not sold or used for
            third-party advertising.
          </p>
        </Section>

        <Section title="Information collected automatically">
          <ul className="list-disc pl-5 space-y-2">
            <li>Device information (device type, operating system, app version).</li>
            <li>Log data (IP address, access times, pages/screens viewed), collected by our infrastructure providers as part of normal request handling.</li>
            <li>Approximate location inferred from IP address, used only for basic analytics — we do not collect precise GPS location.</li>
          </ul>
        </Section>

        <Section title="Who we share data with">
          <p>We don't sell your personal data. We share it only with the service providers that make the app work:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>MongoDB Atlas</strong> — hosts our application database.</li>
            <li><strong>Google (Gemini API)</strong> — processes grocery photos and generates meal plans, as described above.</li>
            <li><strong>PostHog</strong> — provides product analytics and session recording, as described above.</li>
            <li><strong>Cloudflare</strong> — provides DNS, CDN, and network security in front of our servers.</li>
            <li><strong>Hetzner</strong> — hosts the servers our application runs on.</li>
          </ul>
          <p>
            Each of these providers processes data only as needed to provide their service to us, under
            their own privacy and security terms. We may also disclose information if required by law.
          </p>
        </Section>

        <Section title="Payments">
          <p>
            Innate Fitness currently runs on an internal points economy earned through app activity. We do
            not process real-money payments at this time. If paid features are introduced in the future,
            this policy will be updated before that launches, and payment processing will be handled by a
            dedicated, PCI-compliant payment processor — we will never store your card details ourselves.
          </p>
        </Section>

        <Section title="Data retention">
          <p>
            We retain your account and activity data for as long as your account is active, so the app can
            show your history, streaks, and progress. If you delete your account (see below), your personal
            data is removed immediately, except where we're required to retain limited records for legal or
            security purposes.
          </p>
        </Section>

        <Section title="Your rights and choices">
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Access and correction:</strong> most of your data is viewable and editable directly in the app (profile, goals, weight log, etc.).</li>
            <li><strong>Deletion:</strong> you can permanently delete your account and all associated data at any time from Profile → Danger Zone → Delete Account, directly in the app. This takes effect immediately. You can also email <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a> if you need help.</li>
            <li><strong>Analytics opt-out:</strong> to ask us to exclude your account from PostHog analytics/session recording, contact us at the email above.</li>
          </ul>
        </Section>

        <Section title="Children's privacy">
          <p>
            Innate Fitness is not directed at children under 13 (or the equivalent minimum age in your
            jurisdiction), and we do not knowingly collect data from children under that age. If you believe
            a child has created an account, contact us and we will delete it.
          </p>
        </Section>

        <Section title="Security">
          <p>
            We use industry-standard measures to protect your data, including encrypted connections
            (HTTPS/TLS) between your device and our servers, salted password hashing, and token-based
            authentication. No system is perfectly secure, but we work to keep your data protected.
          </p>
        </Section>

        <Section title="Changes to this policy">
          <p>
            If we make material changes to this policy, we'll update the effective date above and, where
            required, notify you in the app. Continued use of Innate Fitness after a change means you
            accept the updated policy.
          </p>
        </Section>

        <Section title="Contact us">
          <p>
            Questions about this policy or your data? Email <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a>.
          </p>
        </Section>
      </div>
    </div>
  );
}
